// ============================================================
//  BEGIN CUSTOM EDIT: Dynamic Cart Recommendations | Date: 2026-02-04
//  What: Fetch and render dynamic product recommendations based on cart contents
// ============================================================

class CartDrawerRecommendations {
  constructor() {
    // Initialize state
    this.cartItemIds = new Set();
    this.currentCartHash = null; // Hash of cart product IDs for cache invalidation
    this.abortController = null;
    this.maxCartItemsToFetch = 5; // Limit parallel requests
    this.container = null;
    this.skeleton = null;
    this.content = null;
    this.list = null;
    this.template = null;
    this.emptyState = null;
    this.intent = 'complementary';
    this.limit = 10;
    this.updateDebounceTimer = null;
    this.latestCart = null;
    this.cartDrawer = null;
    this.cartDrawerObserver = null;
    this.isLoadingInitialCart = false;
    this.initialCartAbortController = null;
    this.loadingCartHash = null;
    this.cartDataVersion = 0;

    this.onCartChange = this.onCartChange.bind(this);
    this.onVariantAdd = this.onVariantAdd.bind(this);
    this.onLineItemChange = this.onLineItemChange.bind(this);
    this.onDrawerAfterShow = this.onDrawerAfterShow.bind(this);
    this.onDrawerAfterHide = this.onDrawerAfterHide.bind(this);
    this.onSectionLoad = this.onSectionLoad.bind(this);

    // Always set up event listeners (container may load later)
    this.init();
  }

  init() {
    // Listen for cart events (IMPACT theme dispatches on documentElement, bubbles to document)
    document.addEventListener('cart:change', this.onCartChange);
    document.addEventListener('variant:add', this.onVariantAdd);
    document.addEventListener('line-item:change', this.onLineItemChange);
    document.addEventListener('dialog:after-show', this.onDrawerAfterShow);
    document.addEventListener('dialog:after-hide', this.onDrawerAfterHide);
    document.addEventListener('shopify:section:load', this.onSectionLoad);

    this.setupCartDrawerObserver();
  }

  setupCartDrawerObserver() {
    this.observeCartDrawer();
  }

  observeCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || cartDrawer === this.cartDrawer) {
      return;
    }

    if (this.cartDrawerObserver) {
      this.cartDrawerObserver.disconnect();
    }

    this.cartDrawer = cartDrawer;
    this.cartDrawerObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName !== 'open' || mutation.oldValue === null) {
          continue;
        }

        if (!cartDrawer.hasAttribute('open')) {
          this.handleDrawerClose();
        }
      }
    });
    this.cartDrawerObserver.observe(cartDrawer, { attributes: true, attributeFilter: ['open'], attributeOldValue: true });

    if (cartDrawer.hasAttribute('open')) {
      this.handleDrawerOpen();
    }
  }

  onDrawerAfterShow(event) {
    if (event.target?.matches?.('cart-drawer')) {
      if (event.target !== this.cartDrawer) {
        if (this.cartDrawerObserver) {
          this.cartDrawerObserver.disconnect();
        }
        this.cartDrawer = null;
        this.observeCartDrawer();
      }
      this.handleDrawerOpen();
    }
  }

  onDrawerAfterHide(event) {
    if (event.target?.matches?.('cart-drawer')) {
      this.handleDrawerClose();
    }
  }

  onSectionLoad(event) {
    if (event.target?.querySelector?.('cart-drawer') || event.target?.matches?.('cart-drawer')) {
      this.observeCartDrawer();
      if (this.isDrawerOpen()) {
        this.handleDrawerOpen();
      }
    }
  }

  isDrawerOpen() {
    const cartDrawer = this.cartDrawer || document.querySelector('cart-drawer');
    return Boolean(cartDrawer?.hasAttribute('open'));
  }

  handleDrawerOpen() {
    this.refreshDomReferences();
    if (!this.container) {
      return;
    }

    if (this.latestCart) {
      this.updateFromCart(this.latestCart);
      return;
    }

    if (!this.currentCartHash) {
      this.loadInitialCart();
    }
  }

  handleDrawerClose() {
    clearTimeout(this.updateDebounceTimer);

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    if (this.initialCartAbortController) {
      this.initialCartAbortController.abort();
      this.initialCartAbortController = null;
    }

    this.isLoadingInitialCart = false;
    this.loadingCartHash = null;
  }

  async loadInitialCart() {
    if (!this.isDrawerOpen() || this.isLoadingInitialCart) {
      return;
    }

    this.isLoadingInitialCart = true;
    const cartDataVersion = this.cartDataVersion;
    const abortController = new AbortController();
    this.initialCartAbortController = abortController;

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
        signal: abortController.signal
      });
      const text = await response.text();

      // Check if response is HTML (bot protection) by looking at first char
      if (!text || text.trim()[0] !== '{') {
        return; // Don't hide - wait for cart events
      }

      const cart = JSON.parse(text);
      if (abortController.signal.aborted || this.initialCartAbortController !== abortController || this.cartDataVersion !== cartDataVersion || !this.isDrawerOpen()) {
        return;
      }

      this.latestCart = cart;
      this.updateFromCart(cart);
    } catch (error) {
      // Don't hide on initial load failure - cart events will provide data
    } finally {
      if (this.initialCartAbortController === abortController) {
        this.initialCartAbortController = null;
        this.isLoadingInitialCart = false;
      }
    }
  }

  onCartChange(event) {
    const cart = event.detail?.cart;
    if (cart) {
      this.handleCartData(cart);
    }
  }

  onVariantAdd(event) {
    const cart = event.detail?.cart;
    if (cart) {
      this.handleCartData(cart);
    }
  }

  onLineItemChange(event) {
    const cart = event.detail?.cart;
    if (cart) {
      this.handleCartData(cart);
    }
  }

  handleCartData(cart) {
    const cartDataVersion = ++this.cartDataVersion;
    this.latestCart = cart;
    if (this.isDrawerOpen()) {
      this.debouncedUpdate(cart, cartDataVersion);
    }
  }

  debouncedUpdate(cart, cartDataVersion = this.cartDataVersion) {
    // Debounce to wait for theme to finish HTML replacement (300ms)
    clearTimeout(this.updateDebounceTimer);
    this.updateDebounceTimer = setTimeout(() => {
      if (this.cartDataVersion === cartDataVersion && this.isDrawerOpen()) {
        this.updateFromCart(cart);
      }
    }, 300);
  }

  updateFromCart(cart) {
    if (!this.isDrawerOpen()) {
      this.latestCart = cart;
      return;
    }

    // Re-query DOM elements (cart drawer HTML may have been replaced)
    this.refreshDomReferences();

    if (!this.container) {
      return;
    }

    if (!cart?.items?.length) {
      this.hide();
      return;
    }

    // Build set of product IDs in cart (for filtering recommendations)
    this.cartItemIds = new Set(cart.items.map(item => item.product_id));

    // Get unique product IDs from cart (max 5 for parallel fetching)
    const uniqueProductIds = [...new Set(cart.items.map(item => item.product_id))];
    const productIdsToFetch = uniqueProductIds.slice(0, this.maxCartItemsToFetch);

    // Create hash of product IDs to detect cart changes
    const newCartHash = [...productIdsToFetch].sort().join(',');

    if (this.loadingCartHash === newCartHash) {
      return;
    }

    // Skip if same cart composition (avoid redundant fetches)
    if (newCartHash === this.currentCartHash) {
      // Check if recommendations still exist (DOM may have been replaced)
      const existingItems = this.list?.querySelectorAll('[data-product-id]');
      if (existingItems && existingItems.length > 0) {
        this.cancelActiveRecommendationRequest();
        // Still filter existing recommendations (in case cart changed)
        if (this.filterExistingRecommendations() > 0) {
          this.showContent();
        }
        return;
      }
      // DOM was replaced, need to refetch
      this.cancelActiveRecommendationRequest();
    }

    this.fetchRecommendationsForMultiple(productIdsToFetch, newCartHash);
  }

  refreshDomReferences() {
    this.container = document.getElementById('cart-drawer-recommendations-container');
    if (!this.container) return;

    // Read settings from container data attributes
    this.intent = this.container.dataset.intent || 'complementary';
    this.limit = parseInt(this.container.dataset.limit, 10) || 10;

    this.skeleton = this.container.querySelector('.cart-drawer__recommendations-skeleton');
    this.content = this.container.querySelector('.cart-drawer__recommendations-content');
    this.list = document.getElementById('cart-drawer-recommendations-list');
    this.template = document.getElementById('horizontal-product-template');
    this.emptyState = this.container.querySelector('.cart-drawer__recommendations-empty');
  }

  cancelActiveRecommendationRequest() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    this.loadingCartHash = null;
  }

  async fetchRecommendationsForMultiple(productIds, cartHash) {
    if (!this.isDrawerOpen()) {
      return;
    }

    // Cancel any pending request
    if (this.abortController) {
      this.abortController.abort();
    }
    const abortController = new AbortController();
    this.abortController = abortController;
    this.loadingCartHash = cartHash;

    this.showSkeleton();

    // Timeout after 8 seconds (longer for multiple parallel requests)
    const timeoutId = setTimeout(() => {
      abortController.abort();
    }, 8000);

    try {
      // Fetch complementary products for ALL cart items in parallel
      const complementaryResults = await Promise.all(
        productIds.map(productId => this.fetchProductsByIntent(productId, 'complementary', abortController.signal))
      );

      // Merge and deduplicate complementary products
      let merged = this.mergeAndDeduplicate(complementaryResults);

      // If less than 2 complementary, fetch related products as fallback
      if (merged.length < 2) {
        const relatedResults = await Promise.all(
          productIds.map(productId => this.fetchProductsByIntent(productId, 'related', abortController.signal))
        );

        const relatedMerged = this.mergeAndDeduplicate(relatedResults);

        // Add related products that aren't already in complementary
        const existingIds = new Set(merged.map(p => p.id));
        const uniqueRelated = relatedMerged.filter(p => !existingIds.has(p.id));
        merged = [...merged, ...uniqueRelated].slice(0, this.limit);
      }

      clearTimeout(timeoutId);

      if (merged.length > 0) {
        // Fetch metafields for shoelaces/shoecare products
        const productsWithMetafields = await this.enrichWithMetafields(merged, abortController.signal);

        if (abortController.signal.aborted || this.abortController !== abortController || !this.isDrawerOpen()) {
          return;
        }

        // Refresh DOM refs right before render (theme may have replaced HTML during fetch)
        this.refreshDomReferences();
        if (this.renderProducts(productsWithMetafields)) {
          this.currentCartHash = cartHash;
        }
      } else {
        if (abortController.signal.aborted || this.abortController !== abortController || !this.isDrawerOpen()) {
          return;
        }
        this.showFallback();
      }
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') return; // Intentional cancellation
      if (abortController.signal.aborted || this.abortController !== abortController || !this.isDrawerOpen()) {
        return;
      }
      this.showFallback();
    } finally {
      if (this.abortController === abortController) {
        this.abortController = null;
        this.loadingCartHash = null;
      }
    }
  }

  /**
   * Merge multiple recommendation arrays and deduplicate by product ID
   * Products appearing in multiple results are prioritized (moved to front)
   */
  mergeAndDeduplicate(resultsArray) {
    const productMap = new Map(); // id -> { product, count }

    // Count occurrences and store product data
    for (const products of resultsArray) {
      for (const product of products) {
        if (productMap.has(product.id)) {
          productMap.get(product.id).count++;
        } else {
          productMap.set(product.id, { product, count: 1 });
        }
      }
    }

    // Convert to array and sort by count (most recommended first), then limit
    return Array.from(productMap.values())
      .sort((a, b) => b.count - a.count)
      .map(item => item.product)
      .slice(0, this.limit);
  }

  async enrichWithMetafields(products, signal) {
    // Fetch metafields for shoelaces/shoecare products via section rendering
    const enrichedProducts = await Promise.all(products.map(async (product) => {
      const tags = product.tags || [];
      const tagString = Array.isArray(tags) ? tags.join(',').toLowerCase() : tags.toLowerCase();

      // Only fetch metafields for shoelaces/shoecare products
      if (!tagString.includes('shoelaces') && !tagString.includes('shoecare')) {
        return product;
      }

      try {
        // Use section rendering to get metafields via Liquid
        const response = await fetch(`${window.Shopify.routes.root}products/${product.handle}?section_id=product-metafields-json`, {
          signal
        });
        if (!response.ok) {
          return product;
        }

        const text = await response.text();
        // Section rendering wraps output in <div id="shopify-section-...">
        // Extract JSON from inside the wrapper
        let jsonString = text.trim();
        const jsonMatch = jsonString.match(/>\s*(\{[\s\S]*\})\s*</);
        if (jsonMatch) {
          jsonString = jsonMatch[1];
        }
        const data = JSON.parse(jsonString.trim());

        // Merge metafields into our product object
        return {
          ...product,
          metafields: {
            product_color: {
              number: data.metafields?.product_color?.number
            },
            announcement: {
              background_color: data.metafields?.announcement?.background_color,
              text_color: data.metafields?.announcement?.text_color
            },
            global: {
              'short-name': data.metafields?.global?.short_name,
              'color-type': data.metafields?.global?.color_type
            }
          }
        };
      } catch (error) {
        return product;
      }
    }));

    return enrichedProducts;
  }

  async fetchProductsByIntent(productId, intent, signal) {
    const url = `${window.Shopify.routes.root}recommendations/products.json?product_id=${productId}&limit=${this.limit}&intent=${intent}`;

    const response = await fetch(url, {
      signal
    });

    // Handle rate limiting
    if (response.status === 429) {
      return [];
    }

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    const products = data.products || [];

    // Filter out products already in cart + unavailable
    return products.filter(product =>
      !this.cartItemIds.has(product.id) && product.available
    );
  }

  renderProducts(products) {
    if (!this.list || !this.template) {
      this.refreshDomReferences();
      if (!this.list || !this.template) {
        return false;
      }
    }

    // Clear existing using safe DOM method
    while (this.list.firstChild) {
      this.list.removeChild(this.list.firstChild);
    }

    // Render each product using template cloning
    products.forEach(product => {
      const card = this.buildProductCard(product);
      if (card) {
        this.list.appendChild(card);
      }
    });

    this.showContent();
    return true;
  }

  buildProductCard(product) {
    if (!this.template || !this.template.content) return null;

    // Clone template for safe DOM construction
    const fragment = this.template.content.cloneNode(true);
    const card = fragment.firstElementChild;
    if (!card) return null;

    const image = product.featured_image || product.images?.[0];
    // Request 160px for retina displays (2x of ~80px container)
    const imageUrl = image ? this.getImageUrl(image, 160) : '';
    const price = this.formatMoney(product.price);
    const comparePrice = product.compare_at_price ? this.formatMoney(product.compare_at_price) : null;
    const hasMultipleVariants = product.variants?.length > 1;
    const variantId = product.variants?.[0]?.id;

    // Check if shoelaces/shoecare product
    const tags = product.tags || [];
    const tagString = Array.isArray(tags) ? tags.join(',').toLowerCase() : tags.toLowerCase();
    const isShoelacesOrShoecare = tagString.includes('shoelaces') || tagString.includes('shoecare');

    // Get metafields if available
    const metafields = product.metafields || {};
    const colorNumber = metafields.product_color?.number?.value || metafields.product_color?.number;
    const badgeBackground = metafields.announcement?.background_color?.value || metafields.announcement?.background_color;
    const badgeTextColor = metafields.announcement?.text_color?.value || metafields.announcement?.text_color;
    const shortName = metafields.global?.['short-name']?.value || metafields.global?.['short-name'];
    const colorType = metafields.global?.['color-type']?.value || metafields.global?.['color-type'];

    // Set data attributes
    card.dataset.productId = product.id;
    card.dataset.productHandle = product.handle;
    card.dataset.productPrice = (product.price / 100).toFixed(2);

    // Set links (textContent for safety)
    const productLink = card.querySelector('[data-product-link]');
    const titleLink = card.querySelector('[data-product-title-link]');
    if (productLink) productLink.href = product.url;
    if (titleLink) {
      titleLink.href = product.url;
      const isShoelaces = tagString.includes('shoelaces');

      if (isShoelaces && (shortName || colorType)) {
        // Shoelaces: show short-name as title, color-type below
        titleLink.textContent = shortName || product.title;
        if (colorType) {
          const colorTypeWrapper = document.createElement('div');
          colorTypeWrapper.className = 'text-xs';
          const colorTypeSpan = document.createElement('span');
          colorTypeSpan.textContent = colorType;
          colorTypeWrapper.appendChild(colorTypeSpan);
          titleLink.parentNode.insertBefore(colorTypeWrapper, titleLink.nextSibling);
        }
      } else if (isShoelacesOrShoecare && (shortName || colorType)) {
        // Shoecare: show short-name - color-type on same line
        const displayName = shortName || product.title;
        titleLink.textContent = colorType ? `${displayName} - ${colorType}` : displayName;
      } else {
        // Other products: show full title
        titleLink.textContent = product.title;
      }

      // Show vendor beneath title for non-shoelaces products (matches line-item.liquid pattern)
      // Note: shoecare products SHOULD show vendor, only shoelaces products should not
      if (!isShoelaces && product.vendor) {
        const vendorWrapper = document.createElement('div');
        vendorWrapper.className = 'text-xs text-subdued';
        const vendorSpan = document.createElement('span');
        vendorSpan.textContent = product.vendor;
        vendorWrapper.appendChild(vendorSpan);
        // Insert after the title link (or after colorType if present)
        titleLink.parentNode.insertBefore(vendorWrapper, titleLink.nextSibling);
      }
    }

    // Set image
    const img = card.querySelector('[data-product-image]');
    const imageWrapper = img?.closest('.horizontal-product__image-wrapper');
    if (img && imageUrl) {
      img.src = imageUrl;
      img.alt = product.title;

      // Add color badge for shoelaces/shoecare if metafields available
      if (isShoelacesOrShoecare && colorNumber && badgeBackground && imageWrapper) {
        const badge = document.createElement('span');
        badge.className = 'horizontal-product__color-badge';

        const needsRing = badgeBackground.includes('#fff') || badgeBackground.includes('#FFF') ||
                          badgeBackground.includes('rgb(255') || badgeBackground.includes('rgba(255, 255, 255');

        const swatch = document.createElement('span');
        swatch.className = `color-badge-swatch-inline${needsRing ? ' ring-inset' : ''}`;
        swatch.style.background = badgeBackground;

        const numberOverlay = document.createElement('span');
        numberOverlay.className = 'swatch-number-overlay';
        numberOverlay.textContent = colorNumber;
        if (badgeTextColor) {
          numberOverlay.style.color = badgeTextColor;
        }

        swatch.appendChild(numberOverlay);
        badge.appendChild(swatch);
        imageWrapper.appendChild(badge);
      }
    } else if (img && !imageUrl) {
      // Remove image wrapper if no image
      if (imageWrapper) imageWrapper.remove();
    }

    // Set price (using textContent for safety)
    const priceList = card.querySelector('[data-price-list]');
    if (priceList) {
      // Clear existing content
      while (priceList.firstChild) {
        priceList.removeChild(priceList.firstChild);
      }

      if (comparePrice) {
        const compareSpan = document.createElement('span');
        compareSpan.className = 'text-xs text-subdued';
        const strikeEl = document.createElement('s');
        strikeEl.textContent = comparePrice;
        compareSpan.appendChild(strikeEl);
        priceList.appendChild(compareSpan);
      }

      const priceSpan = document.createElement('span');
      priceSpan.className = 'text-xs text-subdued';
      priceSpan.textContent = price;
      priceList.appendChild(priceSpan);
    }

    // Set CTA
    const ctaContainer = card.querySelector('[data-cta-container]');
    if (ctaContainer) {
      // Clear existing
      while (ctaContainer.firstChild) {
        ctaContainer.removeChild(ctaContainer.firstChild);
      }

      if (hasMultipleVariants) {
        // Create quick-buy-drawer element
        const quickBuyId = `quick-buy-dynamic-${product.id}`;

        // Create button that opens quick-buy-drawer
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'button button--sm button--subdued';
        button.setAttribute('aria-controls', quickBuyId);

        // Content wrapper (hidden during loading)
        const contentSpan = document.createElement('span');
        contentSpan.className = 'button__content';

        const srSpan = document.createElement('span');
        srSpan.className = 'sr-only';
        srSpan.textContent = product.title;
        contentSpan.appendChild(srSpan);

        // Plus icon
        const iconSpan = document.createElement('span');
        iconSpan.setAttribute('aria-hidden', 'true');
        iconSpan.textContent = '+';
        contentSpan.appendChild(iconSpan);

        button.appendChild(contentSpan);

        // Theme loader (three dots, shown during loading via aria-busy)
        const loader = document.createElement('span');
        loader.className = 'button__loader';
        loader.innerHTML = '<span></span><span></span><span></span>';
        button.appendChild(loader);

        ctaContainer.appendChild(button);

        // Create quick-buy-drawer
        const quickBuy = document.createElement('quick-buy-drawer');
        quickBuy.id = quickBuyId;
        quickBuy.className = 'quick-buy-drawer drawer';
        quickBuy.setAttribute('header-bordered', '');
        quickBuy.setAttribute('open-from', 'bottom');
        quickBuy.setAttribute('handle', `${product.handle}?variant=${variantId}`);
        quickBuy.setAttribute('role', 'region');
        quickBuy.setAttribute('aria-live', 'polite');

        ctaContainer.appendChild(quickBuy);

        // Manually connect button to drawer with loading state
        button.addEventListener('click', () => {
          // Show loading state
          button.setAttribute('aria-busy', 'true');

          // Open the drawer
          if (quickBuy.show) {
            quickBuy.show();
          } else if (quickBuy.open !== undefined) {
            quickBuy.open = true;
          } else {
            quickBuy.setAttribute('open', '');
          }

          // Listen for drawer to finish loading (aria-busy="false" set by theme)
          const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
              if (mutation.attributeName === 'aria-busy' && quickBuy.getAttribute('aria-busy') === 'false') {
                button.removeAttribute('aria-busy');
                observer.disconnect();
                return;
              }
              // Also check if drawer has 'open' attribute (fallback)
              if (mutation.attributeName === 'open' && quickBuy.hasAttribute('open')) {
                // Give a small delay for content to load
                setTimeout(() => {
                  button.removeAttribute('aria-busy');
                  observer.disconnect();
                }, 300);
                return;
              }
            }
          });
          observer.observe(quickBuy, { attributes: true });

          // Fallback timeout in case drawer doesn't trigger expected attributes
          setTimeout(() => {
            button.removeAttribute('aria-busy');
            observer.disconnect();
          }, 3000);
        });
      } else {
        // Create button for single variant (use AJAX add-to-cart)
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'button button--sm button--subdued';
        button.dataset.variantId = variantId;
        button.dataset.productTitle = product.title;

        // Content wrapper (hidden during loading)
        const contentSpan = document.createElement('span');
        contentSpan.className = 'button__content';

        const srSpan = document.createElement('span');
        srSpan.className = 'sr-only';
        srSpan.textContent = product.title;
        contentSpan.appendChild(srSpan);

        const iconSpan = document.createElement('span');
        iconSpan.setAttribute('aria-hidden', 'true');
        iconSpan.textContent = '+';
        contentSpan.appendChild(iconSpan);

        button.appendChild(contentSpan);

        // Theme loader (three dots, shown during loading via aria-busy)
        const loader = document.createElement('span');
        loader.className = 'button__loader';
        loader.innerHTML = '<span></span><span></span><span></span>';
        button.appendChild(loader);

        // Add click handler for AJAX add-to-cart
        button.addEventListener('click', (e) => this.handleAddToCart(e, variantId, product));

        ctaContainer.appendChild(button);
      }
    }

    return card;
  }

  async handleAddToCart(event, variantId) {
    const button = event.currentTarget;

    // Show loading state using theme's aria-busy pattern
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      // Get the cart drawer section ID for section rendering (IMPACT theme format)
      const cartDrawer = document.querySelector('cart-drawer');
      const shopifySection = cartDrawer?.closest('.shopify-section');
      const sectionId = shopifySection?.id?.replace('shopify-section-', '');

      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          sections: sectionId ? [sectionId] : undefined,
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      // Extract line item and sections from response
      const lineItem = data.items ? data.items[0] : data;

      // Fetch updated cart with sections for IMPACT theme
      const cartUrl = new URL(`${window.Shopify.routes.root}cart.js`, window.location.origin);
      if (sectionId) {
        cartUrl.searchParams.set('sections', sectionId);
      }
      const cartResponse = await fetch(cartUrl);
      const cartData = await cartResponse.json();

      // Merge sections from both responses
      const cart = {
        ...cartData,
        sections: { ...(data.sections || {}), ...(cartData.sections || {}) }
      };

      // ============================================================
      //  BEGIN CUSTOM EDIT: Dispatch cart:change instead of cart:refresh | Date: 2026-02-06
      //  What: Use cart:change so FreeShippingBar + cart totals update without extra fetch
      // ============================================================
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'variant:add', cart }
      }));
      document.documentElement.dispatchEvent(new CustomEvent('variant:add', {
        bubbles: true,
        detail: { items: [lineItem], cart }
      }));
      // ============================================================
      //  END CUSTOM EDIT
      // ============================================================

      // Show success briefly - swap content to checkmark
      button.removeAttribute('aria-busy');
      const contentEl = button.querySelector('.button__content');
      if (contentEl) {
        const originalContent = contentEl.innerHTML;
        contentEl.innerHTML = '<span aria-hidden="true">✓</span>';
        setTimeout(() => {
          contentEl.innerHTML = originalContent;
          button.disabled = false;
        }, 1000);
      } else {
        button.disabled = false;
      }

    } catch (error) {
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }

  filterExistingRecommendations() {
    if (!this.list) return 0;

    const items = this.list.querySelectorAll('[data-product-id]');
    let visibleCount = 0;

    items.forEach(item => {
      const productId = parseInt(item.dataset.productId, 10);
      if (this.cartItemIds.has(productId)) {
        item.style.display = 'none';
      } else {
        item.style.display = '';
        visibleCount++;
      }
    });

    if (visibleCount === 0) {
      this.showFallback();
    }

    return visibleCount;
  }

  showFallback() {
    // Hide recommendations if dynamic fails
    this.hide();
  }

  showSkeleton() {
    this.container.dataset.loading = 'true';
    if (this.skeleton) this.skeleton.style.display = '';
    if (this.content) this.content.style.display = 'none';
    if (this.emptyState) this.emptyState.style.display = 'none';
  }

  showContent() {
    if (!this.container) {
      return;
    }
    this.container.dataset.loading = 'false';
    if (this.skeleton) this.skeleton.style.display = 'none';
    if (this.content) {
      this.content.style.display = '';
    }
    if (this.emptyState) this.emptyState.style.display = 'none';

    // Trigger scroll-carousel to recalculate scrollability for navigation buttons
    this.initializeCarouselNavigation();
  }

  initializeCarouselNavigation() {
    const carousel = document.getElementById('cart-drawer-recommendations');
    const list = document.getElementById('cart-drawer-recommendations-list');
    const nav = document.getElementById('cart-drawer-recommendations-nav');
    if (!carousel || !list) return;

    // Wait for next frame to ensure DOM is fully rendered
    requestAnimationFrame(() => {
      const itemCount = list.children.length;

      // Show navigation buttons if more than 1 item
      if (nav) {
        nav.style.display = itemCount > 1 ? '' : 'none';
      }

      // CRITICAL: Clear the carousel's cached _items so it re-queries dynamically added products
      // BaseCarousel caches items on first access, but products are loaded after carousel connects
      carousel._items = null;

      // Re-attach event listeners for prev/next navigation (only once per carousel instance)
      // The carousel's connectedCallback may have been called when items cache was empty,
      // causing the event listeners to not work properly with the new items
      if (carousel.next && carousel.previous && !carousel._dynamicListenersAttached) {
        carousel.addEventListener('control:prev', carousel.previous.bind(carousel));
        carousel.addEventListener('control:next', carousel.next.bind(carousel));
        carousel._dynamicListenersAttached = true;
      }

      // Check if content overflows (for scroll functionality)
      const hasOverflow = carousel.scrollWidth > carousel.clientWidth;
      const isScrollable = itemCount > 1 && hasOverflow;

      carousel.classList.toggle('is-scrollable', isScrollable);

      // Initialize button states (prev disabled at start)
      if (isScrollable) {
        carousel.dispatchEvent(new CustomEvent('scroll:edge-nearing', { bubbles: true, detail: { position: 'start' } }));
      }
    });
  }

  hide() {
    this.container.dataset.loading = 'false';
    if (this.skeleton) this.skeleton.style.display = 'none';
    if (this.content) this.content.style.display = 'none';
    if (this.emptyState) this.emptyState.style.display = 'none';
  }

  getImageUrl(src, width) {
    if (!src) return '';
    // Use Shopify's image transform URL pattern
    if (typeof src === 'string') {
      // Handle Shopify CDN URLs
      if (src.includes('cdn.shopify.com')) {
        return src.replace(/(_\d+x\d+)?(\.[^.]+)$/, `_${width}x$2`);
      }
      return src;
    }
    return src;
  }

  formatMoney(cents) {
    // Use Shopify's money format if available
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }

    // Fallback formatting
    const amount = (cents / 100).toFixed(2);
    const currency = window.Shopify?.currency?.active || 'EUR';

    // Format based on locale
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
        style: 'currency',
        currency: currency
      }).format(cents / 100);
    } catch (e) {
      return `€${amount}`;
    }
  }
}

// Initialize when DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartDrawerRecommendations();
  });
} else {
  new CartDrawerRecommendations();
}

// ============================================================
//  END CUSTOM EDIT
// ============================================================
